import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Scale, TriangleAlert } from 'lucide-react';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLimitedShellCan } from '../../../lib/shell-mode';
import { cn } from '../../../lib/utils';
import { decisionSheetApi, decisionSheetKeys } from '../api';
import { SheetStatusPill } from './labels';

/**
 * Khối "Sổ chốt phương án" trong chi tiết task — APM-DSX-d, plan §11.2. Khuôn
 * `TaskBugLinks`: endpoint riêng (không thêm cột vào `TaskView` cho một tính
 * năng của vài task), khối **tự ẩn** khi rỗng hoặc người xem thiếu
 * `document:read` (403 ⇒ im lặng, không hiện thẻ lỗi cạnh nội dung chính).
 *
 * Mỗi sổ: tên, trạng thái, "x/y đã chốt", nút "Mở sổ". Task **không tự đóng**
 * khi chốt xong (người dùng chốt 2026-10-07, tiền lệ `BugTask`) — câu nhắc
 * đứng ngay dưới danh sách. Người được giao chưa là người chốt (thêm tay rồi
 * bị gỡ, hoặc gắn task trước khi có người được giao) thì nói ngay: mở sổ ra mà
 * không chọn được là thứ khó hiểu nhất.
 *
 * ⚠️ Không nhận `asOf` — liệt kê quan hệ **đang có** giữa một việc và các sổ
 * nó nhúng, không có trục thời gian để tua (cùng lý do với `TaskBugLinks`).
 * Nơi đọc sổ theo thời gian là trang sổ (`sheetAsOf`).
 */
export function TaskDecisionSheets({
  projectId,
  taskId,
}: {
  readonly projectId: string;
  readonly taskId: string;
}): ReactNode {
  const { t } = useTranslation();
  // Limited shell (APM-PSPACE-g): no `document:read` here ⇒ don't make the call at all.
  const mayRead = useLimitedShellCan('document:read', projectId) === true;

  const sheets = useQuery({
    queryKey: decisionSheetKeys.taskSheets(projectId, taskId),
    queryFn: () => decisionSheetApi.taskSheets(projectId, taskId),
    retry: false,
    enabled: mayRead,
  });

  if (!sheets.data || sheets.data.length === 0) return null;

  return (
    <section
      className="flex flex-col gap-2 border-b border-border px-4 py-3"
      aria-label={t('decisionSheet.taskBlock.title')}
      data-testid="task-decision-sheets"
    >
      <h3 className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <Scale aria-hidden className="size-3.5" />
        {t('decisionSheet.taskBlock.title')}
      </h3>
      <ul className="flex flex-col gap-2">
        {sheets.data.map((sheet) => {
          const complete = sheet.questionCount > 0 && sheet.answeredCount === sheet.questionCount;
          return (
            <li
              key={sheet.sheetId}
              className="flex flex-col gap-2 rounded-xl border border-border bg-card px-3 py-2"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="min-w-0 flex-1 basis-40 text-sm font-medium [overflow-wrap:anywhere]">
                  {sheet.title}
                </span>
                <SheetStatusPill status={sheet.status} />
                <span
                  className={cn(
                    'text-xs tabular-nums',
                    complete ? 'text-rag-green' : 'text-muted-foreground',
                  )}
                >
                  {t('decisionSheet.progress', {
                    answered: sheet.answeredCount,
                    total: sheet.questionCount,
                  })}
                </span>
                <Link
                  to="/projects"
                  search={{ projectId, tab: 'overview', sheet: sheet.sheetId }}
                  className="btn-glass rounded-full px-3 py-1 text-xs font-medium"
                >
                  {t('decisionSheet.taskBlock.open')}
                </Link>
              </div>
              {sheet.assigneeIsDecider ? null : (
                <p className="flex items-start gap-1.5 text-xs text-rag-amber">
                  <TriangleAlert aria-hidden className="mt-0.5 size-3.5 shrink-0" />
                  {t('decisionSheet.taskBlock.notDecider')}
                </p>
              )}
            </li>
          );
        })}
      </ul>
      <p className="text-xs text-muted-foreground">{t('decisionSheet.taskBlock.hint')}</p>
    </section>
  );
}
